import { useAuth } from './useAuth';
import type { PermissionCheck, ResourceType } from '../types/auth.types';

export const usePermissions = () => {
    const { user } = useAuth();

    const hasPermission = (check: PermissionCheck): boolean => {
        if (!user) return false;

        // Check direct permissions
        const hasDirectPermission =
            user.permissions?.some(
                (permission) =>
                    permission.resource === check.resource && permission.action === check.action
            ) ?? false;

        if (hasDirectPermission) return true;

        // Check role-based permissions
        const hasRolePermission =
            user.roles?.some((role) =>
                role.permissions.some(
                    (permission) =>
                        permission.resource === check.resource && permission.action === check.action
                )
            ) ?? false;

        return hasRolePermission;
    };

    const hasAnyPermission = (checks: PermissionCheck[]): boolean => {
        return checks.some((check) => hasPermission(check));
    };

    const hasAllPermissions = (checks: PermissionCheck[]): boolean => {
        return checks.every((check) => hasPermission(check));
    };

    const canBrowse = (resource: ResourceType): boolean => {
        return hasPermission({ resource, action: 'browse' });
    };

    const canRead = (resource: ResourceType): boolean => {
        return hasPermission({ resource, action: 'read' });
    };

    const canEdit = (resource: ResourceType): boolean => {
        return hasPermission({ resource, action: 'edit' });
    };

    const canAdd = (resource: ResourceType): boolean => {
        return hasPermission({ resource, action: 'add' });
    };

    const canDelete = (resource: ResourceType): boolean => {
        return hasPermission({ resource, action: 'delete' });
    };

    const canSearch = (resource: ResourceType): boolean => {
        return hasPermission({ resource, action: 'search' });
    };

    return {
        hasPermission,
        hasAnyPermission,
        hasAllPermissions,
        canBrowse,
        canRead,
        canAdd,
        canEdit,
        canDelete,
        canSearch,
        permissions: user?.permissions || [],
    };
};
